import { Component, inject } from '@angular/core';
import { ClientService } from '@lib/client/client.service';
import { ComponentsModule, DialogService, SnackBarService } from '@mastechnicaegroup/carelinelive-ui';
import { Client, ClientSingleResponse, StoreClientRequest } from '@lib/api/models/client';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { createPersonDetailsFormGroup } from '@lib/api/models/person';
import { SendableRequest } from '@mastechnicaegroup/carelinelive-cdk/requests';
import { ErrorPanelComponent } from '@lib/errors/error-panel/error-panel.component';
import { ReactiveFormsModule } from '@angular/forms';

@Component({
    selector: 'app-client-additional-information',
    imports: [
        ComponentsModule,
        ErrorPanelComponent,
        ReactiveFormsModule
    ],
    templateUrl: './edit-client-additional-comments-dialog.component.html',
    standalone: true,
    styleUrl: './edit-client-additional-comments-dialog.component.scss'
})
export class EditClientAdditionalCommentsDialogComponent {
    private readonly _clientService = inject(ClientService);
    private readonly _snackBar = inject(SnackBarService);

    protected readonly client = inject<Client>(DIALOG_DATA);
    protected readonly personDetailsFormGroup = createPersonDetailsFormGroup(this.client.person);
    protected readonly dialogRef = inject<DialogRef>(DialogRef);

    updateClientRequest = new SendableRequest<ClientSingleResponse, StoreClientRequest>({
        request: (payload) => this._clientService.updateClient(this.client.id, payload!),

        onSend: () => {
            this.personDetailsFormGroup.disable();
        },

        onSuccess: () => {
            this.personDetailsFormGroup.enable();
            this._snackBar.open({
                colour: 'success',
                icon: 'check',
                title: 'Updated Additional Comments'
            });
            this.dialogRef.close();
        },

        onError: () => {
            this.personDetailsFormGroup.enable();
        }
    });

    update() {
        if (this.updateClientRequest.loading()) {
            return;
        }

        const clientData: StoreClientRequest = {
            reference: this.client.reference,
            nhs_number: this.client.nhs_number,
            email: this.client.email,
            person: {
                title: this.client.person.title,
                given_name: this.client.person.given_name,
                initials: this.client.person.initials,
                family_name: this.client.person.family_name,
                middle_names: this.client.person.middle_names,
                preferred_name: this.client.person.preferred_name,
                birth_sex: this.client.person.birth_sex,
                pronouns: this.client.person.pronouns,
                date_of_birth: this.client.person.date_of_birth,
                additional_comments: this.personDetailsFormGroup.get('additional_comments')?.value ?? ''
            }
        };

        this.updateClientRequest.send(clientData);
    }

    reset(): void {
        this.personDetailsFormGroup.reset();
    }

    public static show(dialog: DialogService, data: Client) {
        return dialog.openDialog<DialogRef, Client>(EditClientAdditionalCommentsDialogComponent, {
            data
        });
    }
}
